import { useSignOut } from "Auth/Hooks/useSignOut";
import { getApiV1AuthUserinfoOptions } from "Infrastructure/Api/generated/@tanstack/react-query.gen";
import {
	preloadModuleLibrary,
	preloadPathRunList,
	preloadPathTemplateLibrary,
} from "Infrastructure/Routing/AppRouter";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { ClientHeaderSwitcher } from "Modeling/Components/ClientHeaderSwitcher";
import {
	BookOpen,
	Calculator,
	Home,
	LayoutGrid,
	Settings,
	Sparkles,
} from "Shared/appIcons";
import { SelectedClientProvider } from "Shared/Client/SelectedClientContext";
import { useTenantPrimaryColor } from "Shared/Hooks/useTenantPrimaryColor";
import { DrawerNav } from "Shared/Layouts/components/DrawerNav/DrawerNav";
import { DrawerTopbar } from "Shared/Layouts/components/DrawerNav/DrawerTopbar";
import type { NavItem } from "Shared/Layouts/components/NavButton";
import { PageHeader } from "Shared/Layouts/components/PageHeader";
import { TopbarNav } from "Shared/Layouts/components/TopbarNav/TopbarNav";
import { usePageHeaderState } from "Shared/Layouts/PageHeaderContext";
import { LoadingMessage } from "Shared/LoadingMessage";
import { computeInitials } from "Shared/Utils/computeInitials";
import { useQuery } from "@tanstack/react-query";
import { Suspense, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router";

/**
 * Responsive shell composed of two interchangeable navigation patterns:
 * - `TopbarNav` + `PageHeader` for ≥ md viewports (horizontal nav, content row)
 * - `DrawerTopbar` + `DrawerNav` for < md viewports (off-canvas drawer)
 *
 * Mounted once by `AuthenticatedRoute` and wraps every authenticated page via
 * `<Outlet />`. Pages publish their `title` / `subtitle` via `usePageHeader(...)`.
 */
export const AuthenticatedLayout = () => {
	const { t } = useResource();
	const location = useLocation();
	const { title, subtitle, actions } = usePageHeaderState();

	// Drawer state is keyed to the pathname it was opened on. When the user
	// navigates, the pathname changes and the drawer auto-closes.
	const [openForPathname, setOpenForPathname] = useState<string | null>(null);
	const drawerOpen = openForPathname === location.pathname;
	const openDrawer = () => setOpenForPathname(location.pathname);
	const closeDrawer = () => setOpenForPathname(null);

	const documentTitle = title
		? `${title} | ${t(Resources.Common.AppName)}`
		: t(Resources.Common.AppName);

	const { data } = useQuery(getApiV1AuthUserinfoOptions());
	useTenantPrimaryColor(data?.branding?.primaryColor);
	const userLogin = data?.login ?? "";
	const userInitials = computeInitials(userLogin);
	const signOut = useSignOut();

	const navItems: NavItem[] = [
		{
			key: "dashboard",
			label: t(Resources.Sidebar.Dashboard),
			icon: Home,
			to: AppRouting.Dashboard.path(),
			group: "top",
		},
		{
			key: "path-templates",
			label: t(Resources.Sidebar.PathTemplates),
			icon: Sparkles,
			to: AppRouting.PathTemplateLibrary.path(),
			group: "top",
			preload: preloadPathTemplateLibrary,
		},
		{
			key: "module-library",
			label: t(Resources.Sidebar.ModuleLibrary),
			icon: LayoutGrid,
			to: AppRouting.ModuleLibrary.path(),
			group: "top",
			preload: preloadModuleLibrary,
		},
		{
			key: "modelations",
			label: t(Resources.Sidebar.Modelations),
			icon: Calculator,
			to: AppRouting.PathRunList.path(),
			group: "top",
			preload: preloadPathRunList,
		},
		{
			key: "methodology",
			label: t(Resources.Sidebar.Methodology),
			icon: BookOpen,
			group: "bottom",
			disabled: true,
			hint: t(Resources.Sidebar.ComingSoon),
		},
		{
			key: "settings",
			label: t(Resources.Sidebar.Settings),
			icon: Settings,
			group: "bottom",
			disabled: true,
			hint: t(Resources.Sidebar.ComingSoon),
		},
	];

	const isActive = (to?: string) => {
		if (!to) return false;
		if (location.pathname === to) return true;
		return location.pathname.startsWith(`${to}/`);
	};

	// After mount, prefetch the heaviest authenticated chunks during browser
	// idle time. Catches users who keyboard-navigate or click before they
	// hover (mobile, fast clickers). `import(...)` is de-duped by Vite/the
	// browser, so this is a no-op once a chunk is already cached.
	useEffect(() => {
		const ric =
			window.requestIdleCallback ??
			((cb: IdleRequestCallback) =>
				window.setTimeout(() => cb({} as IdleDeadline), 800));
		const cancel = window.cancelIdleCallback ?? window.clearTimeout;
		const handle = ric(() => {
			void preloadPathTemplateLibrary();
			void preloadModuleLibrary();
			void preloadPathRunList();
		});
		return () => cancel(handle as number);
	}, []);

	const handleSignOut = () => signOut();

	const clientSwitcher = <ClientHeaderSwitcher />;

	return (
		<SelectedClientProvider>
			<title>{documentTitle}</title>
			<div className="bg-background text-foreground flex h-screen w-full flex-col font-sans">
				<TopbarNav
					navItems={navItems}
					isActive={isActive}
					userLogin={userLogin}
					userInitials={userInitials}
					onSignOut={handleSignOut}
				/>
				<PageHeader
					title={title}
					subtitle={subtitle}
					actions={actions}
					clientSwitcher={clientSwitcher}
				/>

				<DrawerTopbar
					title={title}
					subtitle={subtitle}
					clientSwitcher={clientSwitcher}
					onOpenMenu={openDrawer}
				/>
				<DrawerNav
					open={drawerOpen}
					onClose={closeDrawer}
					navItems={navItems}
					isActive={isActive}
					userLogin={userLogin}
					userInitials={userInitials}
					onSignOut={handleSignOut}
				/>

				<main className="bg-background min-w-0 flex-1 overflow-y-auto p-6">
					<Suspense fallback={<LoadingMessage />}>
						<Outlet />
					</Suspense>
				</main>
			</div>
		</SelectedClientProvider>
	);
};
